import { com } from '@eidu/web'
import React, { useState } from 'react'
import { useCreateSemanticLink } from '../../io/useSemanticLinks'
import EntityTypeField from '../../pages/permission/path/EntityTypeField'
import AddSemanticLinkForm from './AddSemanticLinkForm'
import CustomSemanticLinkKeyFields from './CustomSemanticLinkKeyFields'
import SemanticLinkFormShell from './SemanticLinkFormShell'
import SemanticLinkTargetFields from './SemanticLinkTargetFields'
import useCustomSemanticLinkKey, { noPredefinedConfigs, predefinedConfigsForKind } from './useCustomSemanticLinkKey'
import EntityType = com.eidu.entity.type.EntityType
import EntityTypeId = com.eidu.entity.type.EntityTypeId
import EntityTypeKind = com.eidu.entity.type.EntityTypeKind
import EntityPath = com.eidu.entity.path.EntityPath
import FieldType = com.eidu.entity.field.FieldType

const isOtherKind = ({ kind }: EntityType) =>
  !kind.equals(EntityTypeKind.Companion.School) && !kind.equals(EntityTypeKind.Companion.User)

type AddOtherSemanticLinkFormProps = {
  entityType?: EntityType
  entityTypes: readonly EntityType[]
  onSave: () => void
  onCancel: () => void
}

const AddOtherSemanticLinkForm = ({ entityType, entityTypes, onSave, onCancel }: AddOtherSemanticLinkFormProps) => {
  const sourceCandidates = entityTypes.filter(isOtherKind)
  const customKey = useCustomSemanticLinkKey(noPredefinedConfigs)

  const [selectedSourceTypeId, setSelectedSourceTypeId] = useState<EntityTypeId>()
  const [fieldType, setFieldType] = useState<FieldType>(FieldType.Reference)
  const [selectedPath, setSelectedPath] = useState<EntityPath>()
  const { save, isSaving } = useCreateSemanticLink(onSave)

  const sourceType =
    entityType ?? sourceCandidates.find(({ id }) => id.equals(selectedSourceTypeId)) ?? sourceCandidates.at(0)

  const sourceTypeField = !entityType && (
    <EntityTypeField
      entityTypeId={selectedSourceTypeId}
      types={sourceCandidates}
      label="Entity type"
      onChange={(type) => {
        setSelectedSourceTypeId(type.id)
        setSelectedPath(undefined)
      }}
    />
  )

  return sourceType && predefinedConfigsForKind(sourceType.kind).length > 0 ? (
    <AddSemanticLinkForm
      key={sourceType.id.toString()}
      entityType={sourceType}
      entityTypes={entityTypes}
      onSave={onSave}
      onCancel={onCancel}
    >
      {sourceTypeField}
    </AddSemanticLinkForm>
  ) : (
    <SemanticLinkFormShell
      isSaving={isSaving}
      canSave={!!selectedPath && !!customKey.resolvedKey}
      onSave={() => customKey.resolvedKey && selectedPath && save(customKey.resolvedKey, selectedPath)}
      onCancel={onCancel}
    >
      {sourceTypeField}
      <CustomSemanticLinkKeyFields
        customKey={customKey}
        fieldType={fieldType}
        onFieldTypeChange={(type) => {
          setFieldType(type)
          setSelectedPath(undefined)
        }}
      />
      {sourceType && (
        <SemanticLinkTargetFields
          key={sourceType.id.toString()}
          sourceTypeId={sourceType.id}
          entityTypes={entityTypes}
          targetTypes={entityTypes}
          fieldType={fieldType}
          selectedPath={selectedPath}
          setSelectedPath={setSelectedPath}
        />
      )}
    </SemanticLinkFormShell>
  )
}

export default AddOtherSemanticLinkForm
export { isOtherKind }
